import type { IdeaRole } from './tradeIdeaVocab'

/**
 * The crosshair guide shown while a trade-idea level is armed: a dashed rule at
 * the pointer's price and a plate saying what to click.
 *
 * When the pointer is on the wrong side of the entry the plate carries the
 * refusal instead — never both. One sentence invites the click; the other
 * refuses it, and showing them together would say nothing clearly. Because the
 * reason is live under the pointer, a click that gets refused is never a click
 * that silently did nothing.
 */

/** Keep the plate off the right edge of the pane. */
const PROMPT_CLAMP = 250
/** A two-line plate stands this far clear of the line it measures. */
const READOUT_GAP = 6
/** Room a two-line plate needs above the line; nearer the pane's top it hangs below instead. */
const READOUT_ROOM = 48

export interface WorkbenchIdeaPromptProps {
  x: number
  y: number
  paneW: number
  text: string
  /** Null while the placement is legal. */
  refusal: string | null
  role: IdeaRole
  color: string
  /** The live measurement under the instruction ("Risk 41.5 pips"); never beside a refusal. */
  readout?: string | null
  /** The dashed rule across the pane. Off while dragging: the level itself is that line. */
  rule?: boolean
  /**
   * The text plate. Off on a phone, where the instruction and the readout are in the strip
   * and a plate would sit under the finger 
   */
  plate?: boolean
  /** The role the level would take ("STOP LOSS"), at the pane's left edge on the rule. */
  lineLabel?: string | null
}

export function WorkbenchIdeaPrompt({
  x,
  y,
  paneW,
  text,
  refusal,
  role,
  color,
  readout = null,
  rule = true,
  plate = true,
  lineLabel = null,
}: WorkbenchIdeaPromptProps) {
  // The guide takes the risk/reward tone while placing — but the level itself
  // always lands in the idea's colour. Tone belongs to the thing that vanishes.
  const tone = refusal
    ? 'var(--danger)'
    : role === 'sl'
      ? 'var(--danger)'
      : role === 'tp'
        ? 'var(--success)'
        : color
  const measuring = !refusal && !!readout
  // A one-line plate sits centred on the line. With the readout it sits clear of it,
  // so the line never cuts through the measurement.
  const place = !measuring ? { top: -14 } : y < READOUT_ROOM ? { top: READOUT_GAP } : { bottom: READOUT_GAP }

  return (
    <div className="pointer-events-none absolute inset-0 z-[105]" data-idea-prompt>
      <div
        className="absolute left-0 right-0"
        style={{ top: y, borderTop: rule ? `1px dashed ${tone}` : undefined }}
      >
        {lineLabel && (
          <span
            className="absolute bottom-0.5 left-1 whitespace-nowrap text-eyebrow font-medium"
            style={{ color: tone }}
            data-idea-line-label
          >
            {lineLabel}
          </span>
        )}
        {plate && (
          <div
            className="absolute whitespace-nowrap rounded px-2 py-1 text-caption text-white"
            style={{
              left: Math.min(x + 14, Math.max(0, paneW - PROMPT_CLAMP)),
              ...place,
              // Mixed toward black rather than a surface token, so the plate stays
              // readable in light mode instead of washing out.
              background: `color-mix(in srgb, ${tone} 82%, black)`,
              border: `1px solid ${tone}`,
            }}
          >
            {refusal ?? (
              <>
                {text}
                {readout && (
                  <div className="text-white/70" data-idea-readout>
                    {readout}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
